/**
 * ControlsSection Component
 * 
 * Control panel component for office employee payslip preview.
 * Provides UI controls for:
 * - Toggling between salary (給与) and bonus (賞与) data types
 * - Selecting year, month (for salary), or period (for bonus)
 * - Searching and selecting employees (admin view only)
 * - Clearing form inputs
 * - Generating payslip table
 * - Downloading payslip as PDF
 * - Opening settings modal (admin view only)
 * 
 * Features:
 * - Different UI for salary vs bonus (month selector vs period selector)
 * - Employee view mode (hides employee code input and settings icon)
 * - PDF button is disabled when required fields are missing
 * - Period display name formatting for bonus periods
 * 
 * @component
 */

import React from 'react';
import { SettingsIcon } from './SettingsIcon';
import { EmployeeAutocompleteInput } from './EmployeeAutocompleteInput';

/**
 * Props interface for ControlsSection component.
 */
interface ControlsSectionProps {
  dataType: 'salary' | 'bonus';                              // Current payslip type
  selectedYear: string;                                       // Selected year
  selectedMonth: string;                                      // Selected month (for salary)
  selectedPeriod: string;                                     // Selected period (for bonus)
  empCode: string;                                            // Employee code input value
  showTable: boolean;                                         // Whether payslip table is displayed
  availableYears: number[];                                   // Available years for selection
  availableMonths: number[];                                  // Available months for selection (salary)
  availablePeriods: string[];                                 // Available periods for selection (bonus)
  onDataTypeChange: (type: 'salary' | 'bonus') => void;      // Handler for data type toggle
  onYearChange: (year: string) => void;                      // Handler for year selection
  onMonthChange: (month: string) => void;                    // Handler for month selection (salary)
  onPeriodChange: (period: string) => void;                  // Handler for period selection (bonus)
  onEmpCodeChange: (code: string) => void;                   // Handler for employee code input change
  onEmpCodeSelect?: (empCode: string, empName: string) => void; // Handler for employee selection from autocomplete
  onClear: () => void;                                        // Handler for clear button
  onPdfDownload: () => void;                                 // Handler for PDF download button
  onSettingsClick: () => void;                               // Handler for settings icon click
  getPeriodDisplayName?: (period: string) => string;         // Optional function to format period display names
  isEmployeeView?: boolean;                                   // Whether this is employee view (limited access)
}

/**
 * ControlsSection Component
 * 
 * Renders the control panel with filters and action buttons for payslip generation.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const ControlsSection: React.FC<ControlsSectionProps> = ({
  dataType,
  selectedYear,
  selectedMonth,
  selectedPeriod,
  empCode,
  showTable,
  availableYears,
  availableMonths,
  availablePeriods,
  onDataTypeChange,
  onYearChange,
  onMonthChange,
  onPeriodChange,
  onEmpCodeChange,
  onEmpCodeSelect,
  onClear,
  onPdfDownload,
  onSettingsClick,
  getPeriodDisplayName,
  isEmployeeView = false
}) => {
  /**
   * Use available years from database, fallback to current year if none available.
   * This ensures the year dropdown always has at least one option.
   */
  const years = availableYears.length > 0 ? availableYears : [new Date().getFullYear()];
  
  /**
   * Use available months from database for salary, fallback to 1-12 if none available.
   * Only applies to salary mode (bonus uses periods instead).
   */
  const months = dataType === 'salary' && availableMonths.length > 0 
    ? availableMonths 
    : Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div className="controls-section">
      {/* Toggle Button for Salary/Bonus - switches between 給与データ and 賞与データ */}
      <div className="data-type-toggle">
        <button
          className={`toggle-btn ${dataType === 'salary' ? 'active' : ''}`}
          onClick={() => onDataTypeChange('salary')}
        >
          給与データ
        </button>
        <button
          className={`toggle-btn ${dataType === 'bonus' ? 'active' : ''}`}
          onClick={() => onDataTypeChange('bonus')}
        >
          賞与データ
        </button>
      </div>

      {/* Month, Year Selection, Employee Code Input and Action Buttons */}
      <div className="input-row">
        <div className="input-group">
          {/* Year Selection Dropdown */}
          <div className="date-field">
            <label htmlFor="year-select">年:</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => onYearChange(e.target.value)}
              className="date-select"
            >
              <option value="">年を選択</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Month (Salary) or Period (Bonus) Selection Dropdown */}
          <div className="date-field">
            <label htmlFor="month-select">{dataType === 'salary' ? '月:' : '期:'}</label>
            {dataType === 'salary' ? (
              /**
               * Salary mode: Month selector (1-12月).
               * Shows available months from database or falls back to 1-12.
               */
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => onMonthChange(e.target.value)}
                className="date-select"
              >
                <option value="">月を選択</option>
                {months.map(month => (
                  <option key={month} value={month}>{month}月</option>
                ))}
              </select>
            ) : (
              /**
               * Bonus mode: Period selector.
               * Uses period display name formatter if provided, otherwise shows raw period value.
               */
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={(e) => onPeriodChange(e.target.value)}
                className="date-select"
              >
                <option value="">期を選択</option>
                {availablePeriods.map(period => (
                  <option key={period} value={period}>
                    {getPeriodDisplayName ? getPeriodDisplayName(period) : period}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Employee Code Input - Only shown in admin view */}
          {!isEmployeeView && (
            <div className="emp-code-field">
              <label htmlFor="emp-code">従業員:</label>
              <EmployeeAutocompleteInput
                id="emp-code"
                value={empCode}
                onChange={onEmpCodeChange}
                onSelect={onEmpCodeSelect}
                placeholder="従業員コードまたは名前を入力"
              />
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="button-container">
            {/* Clear Button - Resets all form inputs */}
            <button className="clear-button" onClick={onClear}>
              クリア
            </button>
            
            {/* PDF Download Button - Disabled when required fields are missing */}
            <button 
              className="pdf-button" 
              onClick={onPdfDownload}
              disabled={!showTable || !selectedYear || (dataType === 'salary' ? !selectedMonth : !selectedPeriod) || !empCode}
            >
              PDF出力
            </button>
            
            {/* Settings Icon - Only shown in admin view */}
            {!isEmployeeView && <SettingsIcon onClick={onSettingsClick} />}
          </div>
        </div>
      </div>
    </div>
  );
};
